import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Foldable block with header (icon + title + count + actions) and a collapsible body.
 * Used by every "related-data" block on CustomerDetail.
 */
export default function FoldBlock({
  icon: Icon,
  iconColor = "#1fb6c1",
  title,
  count,
  defaultOpen = false,
  onAdd,
  addLabel = "Add",
  navigateTo,
  navigateLabel = "Open page",
  testId,
  children,
  emptyHint,
  headerExtra,
  canEdit = false,
  countAccent,
  alwaysRenderChildren = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const showEmpty = !count && !alwaysRenderChildren;
  return (
    <section
      className="hv-card overflow-hidden"
      data-testid={testId}
      data-fold-state={open ? "open" : "closed"}
    >
      <header
        className="px-4 md:px-5 py-3 flex items-center justify-between gap-3 cursor-pointer select-none hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-[#647087] shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#647087] shrink-0" />
          )}
          {Icon && (
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
              style={{
                borderColor: `${iconColor}40`,
                background: `${iconColor}14`,
                color: iconColor,
              }}
            >
              <Icon className="w-4 h-4" />
            </div>
          )}
          <h3 className="font-display text-base md:text-lg font-bold tracking-tight truncate">
            {title}
          </h3>
          <span
            className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border shrink-0 ${
              countAccent ||
              (count
                ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                : "border-white/10 text-[#647087] bg-white/[0.02]")
            }`}
            data-testid={`${testId}-count`}
          >
            {count ?? 0}
          </span>
          {headerExtra}
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {navigateTo && (
            <Link
              to={navigateTo}
              className="hv-btn hv-btn-ghost px-2 py-1 text-xs hidden sm:inline-flex"
              title={navigateLabel}
              data-testid={`${testId}-nav`}
            >
              <ExternalLink className="w-3 h-3" /> {navigateLabel}
            </Link>
          )}
          {canEdit && onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
              data-testid={`${testId}-add`}
              title={addLabel}
            >
              <Plus className="w-3 h-3" /> {addLabel}
            </button>
          )}
        </div>
      </header>
      {open && (
        <div className="px-4 md:px-5 pb-4 pt-1 border-t border-white/5" data-testid={`${testId}-body`}>
          {showEmpty ? (
            <div className="text-sm text-[#647087] py-4">
              {emptyHint || "Nothing here yet."}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}
